/**
 * BabyCareQuickActions
 *
 * Quick action buttons for logging baby care activities.
 * Displays available care types based on user's subscription tier.
 * Note: Active sleep session is handled by ActiveSleepTimerCard component.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Baby,
  Moon,
  Pill,
  Thermometer,
  StickyNote,
  Clock,
  Loader2,
  Droplets,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useBabyCare, CARE_TYPES, CareType } from '../index';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { getBabiesFromPregnancy } from '@/lib/babyUtils';
import { BabyCareEntryModal } from './BabyCareEntryModal';

interface BabyCareQuickActionsProps {
  pregnancyId: string;
  /** When sleep plan is in learn/active mode, sleep button redirects to sleep plan */
  sleepPlanActiveMode?: boolean;
}

export function BabyCareQuickActions({ pregnancyId, sleepPlanActiveMode }: BabyCareQuickActionsProps) {
  const { t } = useTranslation('babyCare');
  const {
    isFeatureEnabled,
    availableCareTypes,
    stats,
    activeSleepSessions,
    lastEndedSleep,
    isLoading,
  } = useBabyCare();
  const { lastCompletedPregnancy } = usePregnancyStore();
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);
  const allBabiesAsleep = activeSleepSessions.length >= babies.length;

  const navigate = useNavigate();
  const [selectedCareType, setSelectedCareType] = useState<CareType | null>(null);

  if (!isFeatureEnabled) {
    return null;
  }

  // Locale-aware relative time formatter
  const formatRelativeTime = (iso: string): string => {
    const lastTime = new Date(iso);
    const now = new Date();
    const minutes = Math.floor((now.getTime() - lastTime.getTime()) / 1000 / 60);

    if (minutes < 60) {
      return t('quickActions.timeSince.minutesAgo', { count: Math.max(0, minutes) });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t('quickActions.timeSince.hoursAgo', { count: hours });
    }
    const days = Math.floor(hours / 24);
    return t('quickActions.timeSince.daysAgo', { count: days });
  };

  // Get time since last entry for each type
  const getTimeSince = (careType: CareType): string | null => {
    if (careType === 'sleep') {
      // No label while a sleep is in progress - ActiveSleepTimerCard takes over
      if (activeSleepSessions.length > 0) return null;
      if (!lastEndedSleep?.ended_at) return null;
      return formatRelativeTime(lastEndedSleep.ended_at);
    }
    const stat = stats.find(s => s.care_type === careType);
    if (!stat?.latest_entry) return null;
    return formatRelativeTime(stat.latest_entry);
  };

  // Handle sleep timer started - refresh page
  const handleSleepTimerStarted = () => {
    window.location.reload();
  };

  // Filter to show only available care types
  const visibleCareTypes = CARE_TYPES.filter(ct => availableCareTypes.includes(ct.type));

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick Action Buttons - colored icons without background */}
      <div className="grid grid-cols-3 gap-3">
        {visibleCareTypes.map(({ type, label }) => {
          const timeSince = getTimeSince(type);
          const isDisabled = type === 'sleep' && allBabiesAsleep;

          // Create icon with color
          const IconComponent = {
            feeding: Baby,
            diaper: Droplets,
            sleep: Moon,
            medication: Pill,
            temperature: Thermometer,
            notes: StickyNote,
          }[type];

          // In learn/active sleep plan mode, redirect sleep to sleep plan page
          const handleClick = () => {
            if (isDisabled) return;
            if (type === 'sleep' && sleepPlanActiveMode) {
              navigate('/tools/sleep-plan');
              return;
            }
            setSelectedCareType(type);
          };

          return (
            <button
              key={type}
              onClick={handleClick}
              disabled={isDisabled}
              className={`rounded-[15px] p-4 flex flex-col items-center text-center transition-all ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/5 active:scale-95'
              }`}
            >
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-2">
                <IconComponent className="w-7 h-7 text-white/25" />
              </div>
              <span className="text-white text-sm font-medium">{label}</span>
              {timeSince && (
                <span className="text-white/50 text-xs mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {timeSince}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Upsell for locked care types */}
      {availableCareTypes.length < CARE_TYPES.length && (
        <p className="text-center text-white/50 text-xs">
          {t('quickActions.premiumHint')}
        </p>
      )}

      {/* Entry Modal */}
      {selectedCareType && (
        <BabyCareEntryModal
          careType={selectedCareType}
          pregnancyId={pregnancyId}
          onClose={() => setSelectedCareType(null)}
          onSleepTimerStarted={handleSleepTimerStarted}
        />
      )}
    </div>
  );
}
